import {gql} from '@apollo/client';

/**
 * Exchange reads.
 *
 * `getExchanges` returns one node per exchange plus, when unscoped, an aggregate
 * node whose `exchange` is null — that node is All markets, never an exchange.
 *
 * Its `instrumentCount` counts ENABLED instruments only. Verified across every
 * type: bonds 14 of 185, equities 192 of 208, IPOs 7 of 16, funds 23 of 36.
 * The selector therefore shows a live count and a total, and the totals come
 * from `getFinancialInstruments` (see EXCHANGE_TOTALS_QUERY below).
 */

/**
 * The cheap form. Sectors, composite index and movers are the expensive parts
 * of this resolver, so anything that only needs counts turns them off.
 */
export const GET_EXCHANGE_SUMMARY = gql`
  query ExchangeSummary($input: GetExchangesInput) {
    getExchanges(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithExchange {
        data {
          instrumentCount
          exchange {
            id
            code
            stockExchangeName
            logoImage
            baseCurrencyCode
            tradeable
          }
        }
      }
    }
  }
`;

/** The full read behind Market Overview. */
export const GET_EXCHANGE_MARKET = gql`
  query ExchangeMarket($input: GetExchangesInput) {
    getExchanges(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithExchange {
        data {
          instrumentCount
          exchange {
            id
            code
            stockExchangeName
            logoImage
            baseCurrencyCode
            tradeable
            numberOfListings
            estimatedSettlementDays
            estimatedProcessingDays
            orderProcessingCutoffTime
            enableLimitOrder
            enableMarketOrder
          }
          performanceToday {
            changePercent
            changeAbsolute
            spark
            asOf
          }
          compositeIndex {
            value
            changePercent
            changeAbsolute
            asOf
            series {
              at
              value
            }
          }
          sectorPerformance {
            sector {
              id
              name
            }
            changePercent
            constituentCount
          }
          # These three return the FinancialInstrument union, so every
          # selection has to sit behind an inline fragment.
          gainers {
            ... on PublicEquity {
              id
              ticker
              name
              pricing {
                unitPrice
                changePercent
              }
            }
          }
          losers {
            ... on PublicEquity {
              id
              ticker
              name
              pricing {
                unitPrice
                changePercent
              }
            }
          }
          mostTraded {
            ... on PublicEquity {
              id
              ticker
              name
              tradingVolume
            }
          }
        }
      }
    }
  }
`;

/**
 * Total instrument counts per exchange, all statuses.
 *
 * `getFinancialInstruments` requires both `assetClassKey` and `instrumentKey`,
 * so there is no single "count everything" call — this builds one aliased
 * field per (exchange × type) plus an unscoped set. 20 aliases resolve in ~2s,
 * which is affordable for a control that loads once per session.
 */
export const buildTotalsQuery = (exchangeIds, types) => {
  if (!types?.length)
    return gql`
      query ExchangeTotals {
        __typename
      }
    `;
  const field = (alias, type, exchangeId) => `
    ${alias}: getFinancialInstruments(
      input: {
        assetClassKey: ${type.assetClassKey}
        instrumentKey: ${type.instrumentKey}
        ${exchangeId ? `exchange: "${exchangeId}"` : ''}
        explore: false
        paginate: true
        limit: 1
        page: 1
      }
    ) {
      ... on ResponseWithFinancialInstruments {
        pagination {
          total
        }
      }
    }`;

  const parts = [];
  types.forEach(type => {
    parts.push(field(`all_${type.instrumentKey}`, type, null));
    exchangeIds.forEach(id => {
      parts.push(field(`e${id}_${type.instrumentKey}`, type, id));
    });
  });

  return gql`query ExchangeTotals {${parts.join('\n')}}`;
};

/** Sums a `buildTotalsQuery` result back into one number per scope. */
export const readTotals = (data, exchangeIds, types) => {
  const sum = prefix =>
    types.reduce(
      (total, type) =>
        total +
        (data?.[`${prefix}${type.instrumentKey}`]?.pagination?.total ?? 0),
      0,
    );
  const totals = {all: sum('all_')};
  exchangeIds.forEach(id => {
    totals[id] = sum(`e${id}_`);
  });
  return totals;
};

/**
 * One exchange, everything the definition screens need.
 *
 * `code` is read on purpose but never offered as an input by the UI — it is
 * the stable natural key market data joins on, and an exchange row can be
 * deleted and recreated with a new `_id` but the same `code`.
 */
export const GET_EXCHANGE_DETAIL = gql`
  query ExchangeDetail($id: [ID!], $language: LanguageEnum) {
    getExchanges(
      input: {
        exchangeId: $id
        language: $language
        includeSectors: false
        includeCompositeIndex: false
        moversLimit: 0
      }
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithExchange {
        data {
          instrumentCount
          exchange {
            id
            code
            stockExchangeName
            logoImage
            website
            CEO
            headquarters
            foundedDate
            baseCurrencyCode
            numberOfListings
            tradeable
            stockPicksEnabled
            language
            description
            overview
            whyInvest
            countries {
              id
              name
            }
            countriesOfOperation {
              id
              name
            }
            multiLang {
              language
              stockExchangeName
              description
              overview
              whyInvest
            }
          }
        }
      }
    }
  }
`;

/**
 * Update is a merge, verified against the gateway: a payload carrying only the
 * definitional half left trading config, narrative content and every
 * multiLang entry untouched. That is what lets Multi Asset and Trading View
 * own different halves of the same record.
 *
 * `baseCurrencyCode` is NON_NULL on the input even though it is definitional,
 * so every caller has to send it back.
 */
export const UPDATE_STOCK_EXCHANGE = gql`
  mutation UpdateStockExchange($id: ID!, $input: StockExchangeInput!) {
    updateStockExchange(id: $id, addStockExchangeInput: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on StockExchange {
        id
        code
        stockExchangeName
      }
    }
  }
`;

export const REMOVE_STOCK_EXCHANGE = gql`
  mutation RemoveStockExchange($id: ID!) {
    removeStockExchange(id: $id) {
      ... on Error {
        message
        statusCode
      }
      ... on StockExchange {
        id
        code
      }
    }
  }
`;

/** Per-type instrument counts for one exchange, for the list and delete guard. */
export const buildVenueBreakdownQuery = (exchangeIds, types) => {
  // `gql` parses eagerly, so an empty selection set throws at build time even
  // when the query is skipped. The exchange list is empty on first render.
  if (!exchangeIds?.length)
    return gql`
      query VenueBreakdown {
        __typename
      }
    `;
  const parts = [];
  exchangeIds.forEach(id => {
    types.forEach(type => {
      parts.push(`
      e${id}_${type.instrumentKey}: getExchanges(
        input: {
          exchangeId: ["${id}"]
          assetClassKey: [${type.assetClassKey}]
          instrumentKey: [${type.instrumentKey}]
          includeSectors: false
          includeCompositeIndex: false
          moversLimit: 0
        }
      ) {
        ... on ResponseWithExchange {
          data {
            instrumentCount
            exchange {
              id
            }
          }
        }
      }`);
    });
  });
  return gql`query VenueBreakdown {${parts.join('\n')}}`;
};

/** Reads a `buildVenueBreakdownQuery` result into {exchangeId: {TYPE: n}}. */
export const readVenueBreakdown = (data, exchangeIds, types) => {
  const out = {};
  exchangeIds.forEach(id => {
    out[id] = {};
    types.forEach(type => {
      const nodes = data?.[`e${id}_${type.instrumentKey}`]?.data ?? [];
      // Scoped to one exchange the response still carries an aggregate node with
      // a null exchange; the exchange's own node is the one with a match.
      const own = nodes.find(node => node.exchange?.id === id);
      out[id][type.instrumentKey] = own?.instrumentCount ?? 0;
    });
  });
  return out;
};

/**
 * The trading half of an exchange — everything that changes order behaviour
 * for every instrument listed on it.
 *
 * Read separately from GET_EXCHANGE_DETAIL so the two owning sections stay
 * independent: Multi Asset never asks for settlement days, Trading View never
 * asks for narrative content.
 */
export const GET_EXCHANGE_TRADING_CONFIG = gql`
  query ExchangeTradingConfig($id: [ID!]) {
    getExchanges(
      input: {
        exchangeId: $id
        includeSectors: false
        includeCompositeIndex: false
        moversLimit: 0
      }
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithExchange {
        data {
          instrumentCount
          exchange {
            id
            code
            stockExchangeName
            baseCurrencyCode
            tradeable
            fractionalShares
            enableLimitOrder
            enableMarketOrder
            orderExpirationLimit
            orderProcessingCutoffTime
            estimatedSettlementDays
            estimatedProcessingDays
            defaultClosureTitle
            defaultClosureMessage
            autoSettlementConfig {
              autoSettlement
              daysAfterFill
              settlementTime
            }
            feeSchedule {
              feeName
              feeType
              fixedFeeAmount
              variableFeeAmount
              level
              perExchange
            }
            closure {
              title
              message
              isMarketOpen
            }
            # Instrument v2 per-type overrides. This list is sparse —
            # an entry carries only what differs from the exchange default, and
            # everything else comes back null. It is what an edit re-sends.
            instrumentConfigs {
              ...ExchangeInstrumentConfigFields
            }
            # The exchange default with each type's override merged over it.
            # Read this for display — never merge the two here, the precedence
            # rule is implemented once on the server so it cannot drift.
            PUBLIC_EQUITY: effectiveInstrumentConfig(
              instrumentKey: PUBLIC_EQUITY
            ) {
              ...ExchangeInstrumentConfigFields
            }
            BOND: effectiveInstrumentConfig(instrumentKey: BOND) {
              ...ExchangeInstrumentConfigFields
            }
            MUTUAL_FUND: effectiveInstrumentConfig(instrumentKey: MUTUAL_FUND) {
              ...ExchangeInstrumentConfigFields
            }
            IPO: effectiveInstrumentConfig(instrumentKey: IPO) {
              ...ExchangeInstrumentConfigFields
            }
          }
        }
      }
    }
  }

  fragment ExchangeInstrumentConfigFields on ExchangeInstrumentConfig {
    instrumentKey
    tradeable
    enableMarketOrder
    enableLimitOrder
    fractionalShares
    orderExpirationLimit
    orderProcessingCutoffTime
    estimatedSettlementDays
    estimatedProcessingDays
    # Names exactly which fields fell through to the exchange default, so the
    # UI never compares values to decide what is inherited.
    inheritedFields
    feeSchedule {
      feeName
      feeType
      fixedFeeAmount
      variableFeeAmount
      level
      perExchange
    }
  }
`;

/**
 * The fee schedule an instrument should start from.
 *
 * An instrument listed on an exchange charges that exchange's fees unless
 * someone deliberately gives it its own, so the instrument editor seeds itself
 * from here rather than opening empty. `effectiveInstrumentConfig` already has
 * the per-type override merged over the exchange default, so asking for the
 * instrument's own key is enough — no precedence logic on this side.
 */
export const GET_EXCHANGE_DEFAULT_FEES = gql`
  query ExchangeDefaultFees($id: [ID!], $instrumentKey: InstrumentKey!) {
    getExchanges(
      input: {
        exchangeId: $id
        includeSectors: false
        includeCompositeIndex: false
        moversLimit: 0
      }
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithExchange {
        data {
          exchange {
            id
            code
            effectiveInstrumentConfig(instrumentKey: $instrumentKey) {
              feeSchedule {
                feeName
                feeType
                fixedFeeAmount
                variableFeeAmount
                level
                perExchange
              }
            }
          }
        }
      }
    }
  }
`;
